import { apiRoutes } from '@/constants/api-routes';
import type { ReaderAvatarSlug } from '@/constants/reader-avatars';
import { apiRequest } from './http';
import type { ReaderLabelView, ReaderView } from '@/types/api';

/**
 * Matches the server's `CreateReaderDto`: a display name plus an optional
 * bundled animal avatar.
 */
export interface CreateReaderPayload {
  name: string;
  avatarSlug?: ReaderAvatarSlug;
}

/** Matches the server's `UpdateReaderDto`. */
export interface UpdateReaderPayload {
  name?: string;
  avatarSlug?: ReaderAvatarSlug;
}

/**
 * Matches the server's `SetReaderLabelsDto`: a single full replacement
 * array of label ids covering both interests and reading levels (the join
 * table doesn't distinguish — the `labelType` of each row in `book_labels`
 * is what sorts them into the buckets on read).
 */
export interface SetReaderLabelsPayload {
  labelIds: string[];
}

export interface ReaderLabelsResponse {
  interests: ReaderLabelView[];
  readingLevels: ReaderLabelView[];
}

export const readersService = {
  list() {
    return apiRequest<ReaderView[]>(apiRoutes.readers.list);
  },
  create(payload: CreateReaderPayload) {
    return apiRequest<ReaderView>(apiRoutes.readers.create, {
      method: 'POST',
      body: payload,
    });
  },
  update(id: string, payload: UpdateReaderPayload) {
    return apiRequest<ReaderView>(apiRoutes.readers.one(id), {
      method: 'PATCH',
      body: payload,
    });
  },
  remove(id: string) {
    return apiRequest<void>(apiRoutes.readers.one(id), { method: 'DELETE' });
  },
  getLabels(id: string) {
    return apiRequest<ReaderLabelsResponse>(apiRoutes.readers.labels(id));
  },
  setLabels(id: string, payload: SetReaderLabelsPayload) {
    return apiRequest<ReaderLabelsResponse>(apiRoutes.readers.labels(id), {
      method: 'PUT',
      body: payload,
    });
  },
};
